import React, { useEffect } from 'react';
import {
  RouteName, SUPPORTED_LANGS, Lang, canonicalUrlFor, ogLocaleFor,
} from '../routes/routesConfig';

// Define types for SEO configurations
interface SEOConfigItem {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  hreflang?: { [key: string]: string };
  structuredData?: Record<string, any>;
  // Bing-specific fields
  bingDescription?: string; // Optimized for Bing with exact-match keywords
  author?: string;
  socialSignals?: {
    facebookUrl?: string;
    twitterUrl?: string;
    linkedinUrl?: string;
    instagramUrl?: string;
  };
}

interface SEOLanguageConfig {
  fi: SEOConfigItem;
  sv: SEOConfigItem;
  de: SEOConfigItem;
  en: SEOConfigItem;
}

interface SEOConfigType {
  homepage: SEOLanguageConfig;
  waterMeters: SEOLanguageConfig;
  climaPri: SEOLanguageConfig;
  smokeDetectors: SEOLanguageConfig;
  myMetec: SEOConfigItem;
  aboutUs: SEOConfigItem;
  contact: SEOConfigItem;
  careers: SEOConfigItem;
}

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: Record<string, any>;
  noIndex?: boolean;
  hreflang?: { [lang: string]: string };
  language?: Lang;
  pageType?: keyof SEOConfigType;
  /** Route name from routesConfig. When set, SEOUtils auto-derives the
   *  canonical URL (per-language host + localised slug) and the hreflang
   *  block — no need to pass them in manually. */
  routeName?: RouteName;
  // Bing-specific props
  bingDescription?: string;
  author?: string;
  socialSignals?: {
    facebookUrl?: string;
    twitterUrl?: string;
    linkedinUrl?: string;
    instagramUrl?: string;
  };
}

// Structured data configurations
const structuredDataConfigs = {
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ClimaPri Oy",
    "url": "https://climapri.com",
    "logo": "https://climapri.com/logo.png",
    "sameAs": [
      "https://www.facebook.com/climapri/?locale=fi_FI",
      "https://www.instagram.com/climapri_oy/",
      "https://www.linkedin.com/company/climapri-oy/"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      // The company switchboard, matching Contact.tsx's LocalBusiness block
      // and the number shown in the contact form's error message. This
      // previously read +358 10 4236 030, which appears nowhere else on the
      // site — worth confirming with sales whether that line still exists.
      "telephone": "+358 20 7032 970",
      "contactType": "sales",
      "email": "sales@climapri.com",
      "availableLanguage": ["Finnish", "English", "Swedish", "German"]
    }
  },
  waterMeters: {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": {
      "fi": "Vesimittarijärjestelmä ja älykkäät vesimittarit",
      "sv": "Vattenmätarsystem och smarta vattenmätare",
      "de": "Wasserzählersystem und intelligente Wasserzähler",
      "en": "Water Meter Systems and Smart Water Meters"
    },
    "description": {
      "fi": "Kattavat vesimittarijärjestelmät ja vedenmittausratkaisut langattomalla teknologialla, etäluvulla ja vuotohälytyksillä",
      "sv": "Omfattande vattenmätarsystem och vattenmätningslösningar med trådlös teknik, fjärravläsning och läckagevarningar",
      "de": "Umfassende Wasserzählersysteme und Wassermessungslösungen mit drahtloser Technologie, Fernablesung und Leckagealarmen",
      "en": "Comprehensive water meter systems and measurement solutions with wireless technology, remote reading and leak alerts"
    },
    "brand": {
      "@type": "Brand",
      "name": "ClimaPri"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "ClimaPri Oy",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "FI",
        "addressLocality": "Helsinki"
      }
    },
    "category": "Water Measurement Equipment",
    "keywords": {
      "fi": "vesimittarijärjestelmä, vedenmittaus, vesimittarit, älykkäät vesimittarit, ultraäänivesimittari, M-Bus vesimittari, langaton vesimittari",
      "sv": "vattenmätarsystem, vattenmätning, vattenmätare, smarta vattenmätare, ultraljudsvattenmätare, M-Bus vattenmätare, trådlös vattenmätare",
      "de": "Wasserzählersystem, Wassermessung, Wasserzähler, intelligente Wasserzähler, Ultraschallwasserzähler, M-Bus Wasserzähler, drahtlose Wasserzähler",
      "en": "water meter systems, water measurement, water meters, smart water meters, ultrasonic water meters, M-Bus water meters, wireless water meters"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR"
    },
    "applicationCategory": "Building Automation"
  },
  climaPri: {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": {
      "fi": "ClimaPri lattialämmityksen ohjaus",
      "sv": "ClimaPri golvvärmesstyrning",
      "de": "ClimaPri Fußbodenheizungssteuerung",
      "en": "ClimaPri Underfloor Heating Control"
    },
    "description": {
      "fi": "Älykäs lattialämmityksen ohjausjärjestelmä langattomilla termostaateilla",
      "sv": "Smart golvvärmestyrningssystem med trådlösa termostater",
      "de": "Intelligentes Fußbodenheizungssystem mit drahtlosen Thermostaten",
      "en": "Smart underfloor heating control system with wireless thermostats"
    },
    "brand": {
      "@type": "Brand",
      "name": "ClimaPri"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "ClimaPri Oy"
    }
  },
  smokeDetectors: {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": {
      "fi": "Palovaroitinjärjestelmä ja älykkäät palovaroittimet",
      "sv": "Brandvarnarsystem och smarta brandvarnare",
      "de": "Rauchmeldesystem und intelligente Rauchmelder",
      "en": "Smoke Detector Systems and Smart Fire Detection"
    },
    "description": {
      "fi": "Kattavat palovaroitinjärjestelmät ja paloturvallisuusratkaisut langattomilla älykkäillä palovaroittimilla, etävalvonnalla ja automaattisilla itsestesteillä",
      "sv": "Omfattande brandvarnarsystem och brandsäkerhetslösningar med trådlösa smarta brandvarnare, fjärrövervakning och automatiska självtester",
      "de": "Umfassende Rauchmeldesysteme und Brandschutzlösungen mit drahtlosen intelligenten Rauchmeldern, Fernüberwachung und automatischen Selbsttests",
      "en": "Comprehensive smoke detector systems and fire safety solutions with wireless smart smoke detectors, remote monitoring and automatic self-testing"
    },
    "brand": {
      "@type": "Brand",
      "name": "ClimaPri"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "ClimaPri Oy",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "FI",
        "addressLocality": "Helsinki"
      }
    },
    "category": "Fire Safety Equipment",
    "keywords": {
      "fi": "palovaroitinjärjestelmä, paloturvallisuusjärjestelmä, älykkäät palovaroittimet, langattomat palovaroittimet, etävalvonta, automaattiset itsetestit, DIN EN 14604",
      "sv": "brandvarnarsystem, brandsäkerhetssystem, smarta brandvarnare, trådlösa brandvarnare, fjärrövervakning, automatiska självtester, DIN EN 14604",
      "de": "Rauchmeldesystem, Brandschutzsystem, intelligente Rauchmelder, drahtlose Rauchmelder, Fernüberwachung, automatische Selbsttests, DIN EN 14604",
      "en": "smoke detector systems, fire safety systems, smart smoke detectors, wireless smoke detectors, remote monitoring, automatic self-testing, DIN EN 14604"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR"
    },
    "applicationCategory": "Fire Safety & Building Security"
  }
};

// Helper function to get localized structured data
const getLocalizedStructuredData = (type: keyof typeof structuredDataConfigs, language: keyof SEOLanguageConfig) => {
  const data = JSON.parse(JSON.stringify(structuredDataConfigs[type]));
  
  // Localize name and description if they exist
  if ('name' in data && typeof data.name === 'object') {
    const localizedName = data.name[language];
    data.name = localizedName;
  }
  if ('description' in data && typeof data.description === 'object') {
    const localizedDescription = data.description[language];
    data.description = localizedDescription;
  }
  
  return data;
};

// Type assertion for SEO configurations
const SEOConfigs = {
  homepage: {
    fi: {
      title: "ClimaPri IoT-ratkaisut - Älykkäät vesimittarit, kiinteistöautomaatio & energianhallinta | Suomi",
      description: "Johtavat suomalaiset IoT-ratkaisut älykkäisiin rakennuksiin. ClimaPri tarjoaa langattomat vesimittarit, lattialämmityksen ohjauksen (ClimaPri), palovaroittimet ja energianhallintajärjestelmät.",
      bingDescription: "Älykkäät vesimittarit ja IoT kiinteistöautomaatio Suomessa. ClimaPri ClimaPri lattialämmityksen ohjaus, ClimaPri Cloud platform, langattomat palovaroittimet ja energianhallintajärjestelmät.",
      keywords: "älykkäät vesimittarit, IoT kiinteistöautomaatio, lattialämmityksen ohjaus, huoneistojen mittausjärjestelmät, kiinteistöjen energianhallinta, palovaroittimet, kiinteistöhallinta IoT, ClimaPri, ClimaPri Cloud, langattomat anturit, energiatehokkuus",
      canonicalUrl: "https://climapri.com/fi/",
      author: "ClimaPri Oy Expert Team",
      socialSignals: {
        facebookUrl: "https://www.facebook.com/climapri/?locale=fi_FI",
        instagramUrl: "https://www.instagram.com/climapri_oy/",
        linkedinUrl: "https://www.linkedin.com/company/climapri-oy/"
      },
      hreflang: {
        "fi": "https://climapri.com/fi/",
        "sv": "https://climapri.com/sv/",
        "de": "https://climapri.com/de/",
        "en": "https://climapri.com/"
      },
      structuredData: structuredDataConfigs.organization
    },
    sv: {
      title: "ClimaPri IoT-lösningar - Smarta vattenmätare, fastighetsautomation & energihantering | Sverige",
      description: "Ledande finska IoT-lösningar för smarta byggnader. ClimaPri erbjuder trådlösa vattenmätare, golvvärmesstyrning (ClimaPri), brandvarnare och energihanteringssystem.",
      keywords: "smarta vattenmätare, IoT fastighetsautomation, golvvärmestyrning, lägenhetsmätningssystem, fastighetsenergihantering, brandvarnare, fastighetsförvaltning IoT, ClimaPri, ClimaPri Cloud, trådlösa sensorer, energieffektivitet",
      canonicalUrl: "https://climapri.com/sv/",
      hreflang: {
        "fi": "https://climapri.com/fi/",
        "sv": "https://climapri.com/sv/",
        "de": "https://climapri.com/de/",
        "en": "https://climapri.com/"
      },
      structuredData: structuredDataConfigs.organization
    },
    de: {
      title: "ClimaPri IoT-Lösungen - Intelligente Wasserzähler, Gebäudeautomation & Energiemanagement | Deutschland",
      description: "Führende finnische IoT-Lösungen für intelligente Gebäude. ClimaPri bietet drahtlose Wasserzähler, Fußbodenheizungssteuerung (ClimaPri), Rauchmelder und Energiemanagementsysteme.",
      keywords: "intelligente Wasserzähler, IoT Gebäudeautomation, Fußbodenheizungssteuerung, Wohnungsmesssysteme, Gebäudeenergiemanagement, Rauchmelder, Immobilienmanagement IoT, ClimaPri, ClimaPri Cloud, drahtlose Sensoren, Energieeffizienz",
      canonicalUrl: "https://climapri.com/de/",
      hreflang: {
        "fi": "https://climapri.com/fi/",
        "sv": "https://climapri.com/sv/",
        "de": "https://climapri.com/de/",
        "en": "https://climapri.com/"
      },
      structuredData: structuredDataConfigs.organization
    },
    en: {
      title: "ClimaPri IoT Solutions - Smart Water Meters, Building Automation & Energy Management | Finland",
      description: "Leading Finnish IoT solutions for smart buildings. ClimaPri offers wireless water meters, underfloor heating control (ClimaPri), smoke detectors, and energy management systems.",
      bingDescription: "Smart water meters and IoT building automation in Finland. ClimaPri ClimaPri underfloor heating control, ClimaPri Cloud platform, wireless smoke detectors and energy management systems.",
      keywords: "smart water meters, IoT building automation, underfloor heating control, apartment measurement systems, building energy management, smoke detectors, property management IoT, ClimaPri, ClimaPri Cloud, wireless sensors, energy efficiency",
      canonicalUrl: "https://climapri.com/",
      author: "ClimaPri Oy Expert Team",
      socialSignals: {
        facebookUrl: "https://www.facebook.com/climapri/?locale=fi_FI",
        instagramUrl: "https://www.instagram.com/climapri_oy/",
        linkedinUrl: "https://www.linkedin.com/company/climapri-oy/"
      },
      hreflang: {
        "fi": "https://climapri.com/fi/",
        "sv": "https://climapri.com/sv/",
        "de": "https://climapri.com/de/",
        "en": "https://climapri.com/"
      },
      structuredData: structuredDataConfigs.organization
    }
  },

  waterMeters: {
    fi: {
        title: "Vesimittarijärjestelmä - Älykkäät vesimittarit ja vedenmittausratkaisut | ClimaPri",
      description: "Kattavat vesimittarijärjestelmät ja vedenmittausratkaisut: langattomat ultraäänivesimittarit, M-Bus vesimittarit ja mekaaniset vesimittarit. Tarkka vedenmittaus, etäluku ja vuotohälytykset älykkäällä teknologialla.",
      keywords: "vesimittarijärjestelmä, vedenmittaus järjestelmä, vesimittarit, älykkäät vesimittarit, vedenmittaus, vedenmittausratkaisut, ultraäänivesimittari, M-Bus vesimittari, langaton vesimittari, mekaaninen vesimittari, vesimittarin asennus, etäluenta vesimittari, vuotohälytys, vedenmittauksen automaatio, WM-Bus vesimittari, LoRaWAN vesimittari, vedenmittausjärjestelmä, veden kulutusseuranta, vesimittariteknologia, ClimaPri vesimittarit, ClimaPri Cloud, kiinteistön vedenmittaus, asunto-osakeyhtiön vedenmittaus, huoneistokohtainen vedenmittaus, taloyhtiön vesimittarit, vedenhankinta, vedenkulutuksen mittaus, vedenmittauksen digitalisointi, älykäs vesihuolto, vedenmittauksen etähallinta",
      canonicalUrl: "https://climapri.com/fi/products/water-meters",
      structuredData: getLocalizedStructuredData('waterMeters', 'fi')
    },
    sv: {
      title: "Vattenmätarsystem - Smarta vattenmätare och vattenmätningslösningar | ClimaPri",
      description: "Omfattande vattenmätarsystem och vattenmätningslösningar: trådlösa ultraljudsvattenmätare, M-Bus vattenmätare och mekaniska vattenmätare. Exakt vattenmätning, fjärravläsning och läckagevarningar med smart teknik.",
      keywords: "vattenmätarsystem, vattenmätning system, vattenmätare, smarta vattenmätare, vattenmätning, vattenmätningslösningar, ultraljudsvattenmätare, M-Bus vattenmätare, trådlös vattenmätare, mekanisk vattenmätare, vattenmätarinstallation, fjärravläsning vattenmätare, läckagevarning, vattenmätningsautomation, WM-Bus vattenmätare, LoRaWAN vattenmätare, vattenförbrukningsövervakning, vattenmätarteknik, ClimaPri vattenmätare, ClimaPri Cloud, fastighets vattenmätning, bostadsrättsförenings vattenmätare, lägenhetsspecifik vattenmätning, vattenförsörjning, vattenförbrukningsmätning, digitalisering av vattenmätning, smart vattenförsörjning, fjärrstyrning av vattenmätning",
      canonicalUrl: "https://climapri.com/sv/products/water-meters",
      structuredData: getLocalizedStructuredData('waterMeters', 'sv')
    },
    de: {
      title: "Wasserzählersystem - Intelligente Wasserzähler und Wassermessungslösungen | ClimaPri",
      description: "Umfassende Wasserzählersysteme und Wassermessungslösungen: drahtlose Ultraschallwasserzähler, M-Bus Wasserzähler und mechanische Wasserzähler. Präzise Wassermessung, Fernablesung und Leckagealarme mit intelligenter Technologie.",
      keywords: "Wasserzählersystem, Wassermessung System, Wasserzähler, intelligente Wasserzähler, Wassermessung, Wassermessungslösungen, Ultraschallwasserzähler, M-Bus Wasserzähler, drahtlose Wasserzähler, mechanische Wasserzähler, Wasserzählerinstallation, Fernablesung Wasserzähler, Leckagealarm, Wassermessungsautomation, WM-Bus Wasserzähler, LoRaWAN Wasserzähler, Wasserverbrauchsüberwachung, Wasserzählertechnik, ClimaPri Wasserzähler, ClimaPri Cloud, Gebäude Wassermessung, Eigentumswohnung Wasserzähler, wohnungsspezifische Wassermessung, Wasserversorgung, Wasserverbrauchsmessung, Digitalisierung der Wassermessung, intelligente Wasserversorgung, Fernsteuerung der Wassermessung",
      canonicalUrl: "https://climapri.com/de/products/water-meters",
      structuredData: getLocalizedStructuredData('waterMeters', 'de')
    },
    en: {
      title: "Water Meter Systems - Smart Water Meters & Water Measurement Solutions | ClimaPri",
      description: "Comprehensive water meter systems and water measurement solutions: wireless ultrasonic water meters, M-Bus water meters, and mechanical water meters. Precise water measurement, remote reading, and leak alerts with smart technology.",
      keywords: "water meter systems, water measurement systems, water meters, smart water meters, water measurement, water measurement solutions, ultrasonic water meters, M-Bus water meters, wireless water meters, mechanical water meters, water meter installation, remote reading water meters, leak detection, water measurement automation, WM-Bus water meters, LoRaWAN water meters, water consumption monitoring, water meter technology, ClimaPri water meters, ClimaPri Cloud, building water measurement, apartment water meters, unit-specific water measurement, water supply, water consumption measurement, digitalization of water measurement, smart water management, remote water meter control",
      canonicalUrl: "https://climapri.com/products/water-meters",
      structuredData: getLocalizedStructuredData('waterMeters', 'en')
    }
  },

  climaPri: {
    fi: {
      title: "Lattialämmitysjärjestelmä - ClimaPri älykkäät lattialämmitysratkaisut | ClimaPri",
      description: "Kattavat lattialämmitysjärjestelmät ja älykkäät lattialämmitysratkaisut: ClimaPri langattomilla termostaateilla, e-INK-näytöillä, vuotojen tunnistuksella ja etähallinnalla ClimaPri Cloud-pilvipalvelun kautta.",
      keywords: "lattialämmitysjärjestelmä, älykäs lattialämmitysjärjestelmä, lattialämmitys järjestelmä, lattialämmitysratkaisut, ClimaPri lattialämmitys, älykäs lattialämmitys, lattialämmityksen ohjaus, lattialämmityksen automaatio, langaton lattialämmitysjärjestelmä, älytermostaatit lattialämmitys, huoneistojen lattialämmitys, energiatehokas lattialämmitysjärjestelmä, langaton lämmityksen ohjaus, lattialämmityksen etäohjaus, lattialämmityksen valvonta, lattialämmitysjärjestelmän asennus, jakotukkiohjaus, lattialämmityksen termostaatti, lämpötilansäätö, vesikiertoiset lattialämmitysjärjestelmät, sähköinen lattialämmitysjärjestelmä, taloyhtiön lattialämmitys, asunto-osakeyhtiön lämmitys, kiinteistön lämmitysjärjestelmä, rakennuksen lämmitysautomaatio, ClimaPri Cloud lattialämmitys, ClimaPri e-INK, vuotosensorit lattialämmitys, lämmitysjärjestelmän digitalisointi",
      canonicalUrl: "https://climapri.com/fi/products/climapri-temperature-control",
      structuredData: getLocalizedStructuredData('climaPri', 'fi')
    },
    sv: {
      title: "Golvvärmesystem - ClimaPri smarta golvvärmelösningar | ClimaPri",
      description: "Omfattande golvvärmesystem och smarta golvvärmelösningar: ClimaPri med trådlösa termostater, e-INK-displayer, läckagedetektering och fjärrstyrning via ClimaPri Cloud-molnplattform.",
      keywords: "golvvärmesystem, smart golvvärmesystem, golvvärme system, golvvärmelösningar, ClimaPri golvvärme, smart golvvärme, golvvärmestyrning, golvvärmeautomation, trådlöst golvvärmesystem, smarta termostater golvvärme, lägenhetsgolvvärme, energieffektivt golvvärmesystem, trådlös värmestyrning, fjärrstyrning golvvärme, golvvärmeövervakning, golvvärmesysteminstallation, fördelarkontroll, golvvärmetermostat, temperaturreglering, vattenburna golvvärmesystem, elektriskt golvvärmesystem, bostadsrättsförenings golvvärme, fastighets värmesystem, byggnadsuppvärmningsautomation, ClimaPri Cloud golvvärme, ClimaPri e-INK, läckagesensorer golvvärme, digitalisering av värmesystem",
      canonicalUrl: "https://climapri.com/sv/products/climapri-temperature-control",
      structuredData: getLocalizedStructuredData('climaPri', 'sv')
    },
    de: {
      title: "Fußbodenheizungssystem - ClimaPri intelligente Fußbodenheizungslösungen | ClimaPri",
      description: "Umfassende Fußbodenheizungssysteme und intelligente Fußbodenheizungslösungen: ClimaPri mit drahtlosen Thermostaten, e-INK-Displays, Leckageerkennung und Fernsteuerung über ClimaPri Cloud-Plattform.",
      keywords: "Fußbodenheizungssystem, intelligentes Fußbodenheizungssystem, Fußbodenheizung System, Fußbodenheizungslösungen, ClimaPri Fußbodenheizung, intelligente Fußbodenheizung, Fußbodenheizungssteuerung, Fußbodenheizungsautomation, drahtloses Fußbodenheizungssystem, intelligente Thermostate Fußbodenheizung, Wohnungsfußbodenheizung, energieeffizientes Fußbodenheizungssystem, drahtlose Heizungssteuerung, Fernsteuerung Fußbodenheizung, Fußbodenheizungsüberwachung, Fußbodenheizungssysteminstallation, Verteilerkontrolle, Fußbodenheizungsthermostat, Temperaturregelung, wassergeführte Fußbodenheizungssysteme, elektrisches Fußbodenheizungssystem, Eigentumswohnung Fußbodenheizung, Gebäude Heizsystem, Gebäudeheizungsautomation, ClimaPri Cloud Fußbodenheizung, ClimaPri e-INK, Leckagesensoren Fußbodenheizung, Digitalisierung von Heizsystemen",
      canonicalUrl: "https://climapri.com/de/products/climapri-temperature-control",
      structuredData: getLocalizedStructuredData('climaPri', 'de')
    },
    en: {
      title: "Underfloor Heating Systems - ClimaPri Smart Underfloor Heating Solutions | ClimaPri",
      description: "Comprehensive underfloor heating systems and smart underfloor heating solutions: ClimaPri with wireless thermostats, e-INK displays, leak detection, and remote control via ClimaPri Cloud platform.",
      keywords: "underfloor heating systems, smart underfloor heating systems, underfloor heating system, underfloor heating solutions, ClimaPri underfloor heating, smart underfloor heating, underfloor heating control, underfloor heating automation, wireless underfloor heating system, smart thermostats underfloor heating, apartment underfloor heating, energy efficient underfloor heating system, wireless heating control, remote underfloor heating control, underfloor heating monitoring, underfloor heating system installation, manifold control, underfloor heating thermostat, temperature control, water-based underfloor heating systems, electric underfloor heating system, building heating systems, building heating automation, ClimaPri Cloud underfloor heating, ClimaPri e-INK, leak sensors underfloor heating, digitalization of heating systems",
      canonicalUrl: "https://climapri.com/products/climapri-temperature-control",
      structuredData: getLocalizedStructuredData('climaPri', 'en')
    }
  },

  smokeDetectors: {
    fi: {
      title: "Palovaroitinjärjestelmä - Älykkäät palovaroittimet ja paloturvallisuusratkaisut | ClimaPri",
      description: "Kattavat palovaroitinjärjestelmät ja paloturvallisuusratkaisut: älykkäät langattomat palovaroittimet etävalvonnalla, automaattiset itsetestit ja Suomen 2026 määräysten mukainen vaatimustenmukaisuus.",
      keywords: "palovaroitinjärjestelmä, palovaroitin järjestelmä, paloturvallisuusjärjestelmä, paloturvallisuus järjestelmä, älykkäät palovaroittimet, langattomat palovaroittimet, palovaroittimien valvonta, palovaroitinten etävalvonta, kiinteistön paloturvallisuus, rakennuksen paloturvallisuus, palovaroittimien asennus, palovaroitinhuolto, automaattiset palovaroittimet, verkotetut palovaroittimet, Suomen palovaroitinmääräykset 2026, DIN EN 14604, DIN 14676-1, kiinteistönomistajan vastuu palovaroittimet, asunto-osakeyhtiön palovaroittimet, taloyhtiön paloturvallisuus, WM-Bus palovaroittimet, palovaroittimien keskitetty valvonta, ClimaPri Cloud palovaroittimet, paloturvallisuuden digitalisointi, älykkäät palohälyttimet, palon havaitseminen, savunhavaitseminen",
      canonicalUrl: "https://climapri.com/fi/products/smoke-detectors",
      structuredData: getLocalizedStructuredData('smokeDetectors', 'fi')
    },
    sv: {
      title: "Brandvarnarsystem - Smarta brandvarningssystem och brandsäkerhetslösningar | ClimaPri",
      description: "Omfattande brandvarnarsystem och brandsäkerhetslösningar: smarta trådlösa brandvarnare med fjärrövervakning, automatiska självtester och regelefterlevnad enligt svenska och europeiska standarder.",
      keywords: "brandvarnarsystem, brandvarnarssystem, brandsäkerhetssystem, brandvarningssystem, smarta brandvarnare, trådlösa brandvarnare, brandvarnarövervakning, fjärrövervakning brandvarnare, fastighets brandsäkerhet, byggnads brandsäkerhet, brandvarnarnas installation, brandvarnarservice, automatiska brandvarnare, nätverkade brandvarnare, svenska brandvarnareföreskrifter, DIN EN 14604, DIN 14676-1, fastighetsägarens ansvar brandvarnare, bostadsrättsföreningarnas brandvarnare, husbolags brandsäkerhet, WM-Bus brandvarnare, centraliserad brandvarnarövervakning, ClimaPri Cloud brandvarnare, digitalisering av brandsäkerhet, smarta brandalarm, branddetektering, rökdetektering",
      canonicalUrl: "https://climapri.com/sv/products/smoke-detectors",
      structuredData: getLocalizedStructuredData('smokeDetectors', 'sv')
    },
    de: {
      title: "Rauchmeldesystem - Intelligente Brandmeldesysteme und Brandschutzlösungen | ClimaPri",
      description: "Umfassende Rauchmeldesysteme und Brandschutzlösungen: intelligente drahtlose Rauchmelder mit Fernüberwachung, automatische Selbsttests und Compliance nach deutschen und europäischen Standards.",
      keywords: "Rauchmeldesystem, Rauchmelder System, Brandmeldesystem, Brandschutzsystem, intelligente Rauchmelder, drahtlose Rauchmelder, Rauchmelderüberwachung, Fernüberwachung Rauchmelder, Gebäude Brandschutz, Immobilien Brandschutz, Rauchmelder Installation, Rauchmelder Wartung, automatische Rauchmelder, vernetzte Rauchmelder, deutsche Rauchmelder Vorschriften, DIN EN 14604, DIN 14676-1, Hauseigentümer Verantwortung Rauchmelder, Hausverwaltung Rauchmelder, WM-Bus Rauchmelder, zentrale Rauchmelderüberwachung, ClimaPri Cloud Rauchmelder, Digitalisierung Brandschutz, intelligente Brandmelder, Branderkennung, Raucherkennung",
      canonicalUrl: "https://climapri.com/de/products/smoke-detectors",
      structuredData: getLocalizedStructuredData('smokeDetectors', 'de')
    },
    en: {
      title: "Smoke Detector Systems - Smart Fire Detection Systems and Safety Solutions | ClimaPri",
      description: "Comprehensive smoke detector systems and fire safety solutions: smart wireless smoke detectors with remote monitoring, automatic self-testing, and compliance with European safety standards.",
      keywords: "smoke detector systems, smoke detector system, fire detection systems, fire safety systems, smart smoke detectors, wireless smoke detectors, smoke alarm monitoring, remote smoke monitoring, building fire safety, property fire safety, smoke detector installation, smoke detector maintenance, automatic smoke detectors, networked smoke detectors, European smoke detector standards, DIN EN 14604, DIN 14676-1, property owner responsibilities smoke detectors, building management smoke detectors, WM-Bus smoke detectors, centralized smoke monitoring, ClimaPri Cloud smoke detectors, digitization of fire safety, smart fire alarms, fire detection, smoke detection",
      canonicalUrl: "https://climapri.com/products/smoke-detectors",
      structuredData: getLocalizedStructuredData('smokeDetectors', 'en')
    }
  },

  myMetec: {
    title: "ClimaPri Cloud Platform - IoT Building Management & Remote Monitoring | ClimaPri",
    description: "ClimaPri Cloud platform for comprehensive building automation, remote monitoring, energy management, and IoT device control. Real-time analytics and 3D building visualization.",
    keywords: "ClimaPri Cloud platform, IoT building management, cloud monitoring, building automation platform, energy management software, remote building control, property management technology",
    canonicalUrl: "https://climapri.com/en/climapri-cloud",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "ClimaPri Cloud Platform",
      "description": "Comprehensive IoT platform for building automation and remote monitoring",
      "applicationCategory": "Building Management Software",
      "operatingSystem": "Web-based",
      "provider": {
        "@type": "Organization",
        "name": "ClimaPri Oy"
      }
    }
  },

  aboutUs: {
    title: "About ClimaPri - Finnish IoT Innovation Leader | Smart Building Solutions Since 2017",
    description: "Learn about ClimaPri Oy, Finnish family business pioneering IoT solutions for smart buildings since 2017. Part of WCOM-Group with 20+ years experience in building technology and innovation.",
    keywords: "about ClimaPri, Finnish IoT company, building automation company, smart technology Finland, WCOM-Group, IoT innovation, sustainable buildings",
    canonicalUrl: "https://climapri.com/about-us",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About ClimaPri Oy",
      "description": "Learn about ClimaPri, Finnish family business pioneering IoT solutions for smart buildings",
      "url": "https://climapri.com/about-us",
      "mainEntity": {
        "@type": "Organization",
        "name": "ClimaPri Oy",
        "foundingDate": "2017",
        "description": "Finnish IoT solutions provider specializing in smart building automation"
      }
    }
  },

  contact: {
    title: "Contact ClimaPri - Get Smart Building IoT Solutions | Finland",
    description: "Contact ClimaPri for smart building IoT solutions, water meters, heating control, and automation systems. Expert consultation for property managers, contractors, and building designers.",
    keywords: "contact ClimaPri, IoT solutions consultation, smart building expert, water meter installation, heating control consultation, building automation contact",
    canonicalUrl: "https://climapri.com/contact",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact ClimaPri",
      "description": "Get in touch with ClimaPri for smart building IoT solutions and consultation",
      "url": "https://climapri.com/contact"
    }
  },

  careers: {
    title: "Careers at ClimaPri - Join Our IoT Innovation Team | Smart Building Jobs Finland",
    description: "Join ClimaPri's mission to create sustainable smart building solutions. Explore exciting career opportunities in IoT engineering, sales, product management, and building automation technology.",
    keywords: "careers at ClimaPri, IoT jobs Finland, smart building careers, engineering jobs, product manager jobs, sales manager jobs, building automation careers, technology jobs Helsinki",
    canonicalUrl: "https://climapri.com/careers",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Careers at ClimaPri",
      "description": "Career opportunities at ClimaPri - Join our IoT innovation team",
      "url": "https://climapri.com/careers",
      "mainEntity": {
        "@type": "Organization",
        "name": "ClimaPri Oy",
        "description": "Finnish IoT solutions provider seeking talented professionals"
      }
    }
  }
} as SEOConfigType;

// Enhanced SEO Component with Bing Optimization
export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterTitle,
  twitterDescription,
  twitterImage,
  structuredData,
  noIndex = false,
  hreflang,
  language = 'en',
  pageType,
  routeName,
  bingDescription,
  author,
  socialSignals
}) => {
  useEffect(() => {
    // Keep the <html lang="..."> attribute in sync with the page's
    // language so screen readers, search engines and CSS :lang() match.
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', language);
    }

    // When a routeName is provided, derive the canonical URL and the
    // hreflang block automatically from routesConfig. The per-language
    // host (climapri.fi for Finnish, climapri.se for Swedish, climapri.pl
    // for Polish, climapri.com for English/German) is baked in there.
    const derivedCanonical = routeName
      ? canonicalUrlFor(routeName, language)
      : undefined;
    const derivedHreflang: Record<string, string> | undefined = routeName
      ? (SUPPORTED_LANGS as readonly Lang[]).filter(l => l !== 'pl').reduce((acc, l) => {
          acc[l] = canonicalUrlFor(routeName, l);
          return acc;
        }, {} as Record<string, string>)
      : undefined;
    // Get language-specific SEO config if pageType is provided
    let config: SEOConfigItem | null = null;
    if (pageType) {
      const pageConfig = SEOConfigs[pageType];
      // Check if it's a language config (has language properties) or a single config item
      if (typeof pageConfig === 'object' && 'fi' in pageConfig && language) {
        // SEOLanguageConfig has no Polish entry; fall back to English so the
        // typed lookup is total for every Lang (Polish is a separate microsite).
        config = (pageConfig as SEOLanguageConfig)[language as keyof SEOLanguageConfig]
          ?? (pageConfig as SEOLanguageConfig).en;
      } else if (typeof pageConfig === 'object' && 'title' in pageConfig) {
        config = pageConfig as SEOConfigItem;
      }
    }

    // Title, description and the social tags are emitted per route and per
    // language by generateMetadata into the static HTML (see src/utils/seoData.ts
    // and seoDataDerived.ts, which now cover every page). The client must NOT
    // rewrite them: the per-component configs here are older and partly
    // English-only, so they replaced correct localised copy after load — a
    // Danish contact page ended up titled "Kontakt o" with an English
    // description. Same reasoning as canonical and hreflang below.

    // Update meta tags with Bing optimization
    const updateMetaTag = (name: string, content: string | undefined, property = false) => {
      if (!content) return;
      
      const attribute = property ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, name);
        document.head.appendChild(tag);
      }
      
      tag.setAttribute('content', content);
    };

    // Canonical URL is emitted authoritatively by Next's generateMetadata into
    // the static HTML for every route. The client must NOT manage it — doing so
    // previously produced a SECOND, conflicting <link rel="canonical"> (e.g.
    // climapri.com/ alongside the correct climapri.com/en), which Lighthouse and
    // Google flag as conflicting canonicals. Left to the server-rendered tag.

    // Language meta tag
    updateMetaTag('language', language);
    
    // Robots meta tag with Bing-specific directives
    const robotsContent = noIndex 
      ? 'noindex, nofollow' 
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
    updateMetaTag('robots', robotsContent);
    
    // Bing-specific meta tags
    updateMetaTag('msvalidate.01', process.env.REACT_APP_BING_VERIFICATION || ''); // Bing verification
    updateMetaTag('bingbot', 'index, follow');
    updateMetaTag('msnbot', 'index, follow');
    
    // Author information (important for Bing credibility)
    if (config?.author || author) {
      updateMetaTag('author', config?.author || author);
      updateMetaTag('article:author', config?.author || author, true);
    }
    
    // Publication info for Bing
    updateMetaTag('article:publisher', 'ClimaPri Oy', true);
    updateMetaTag('article:published_time', new Date().toISOString(), true);
    updateMetaTag('article:modified_time', new Date().toISOString(), true);

    // Open Graph image and type (title, description and url come from the server)
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:type', ogType, true);
    // og:url is emitted by generateMetadata (openGraph.url) in the static HTML;
    // don't override it client-side.
    updateMetaTag('og:locale', ogLocaleFor(language), true);
    updateMetaTag('og:site_name', 'ClimaPri IoT Solutions', true);

    // Enhanced social media tags for Bing social signals
    if (config?.socialSignals || socialSignals) {
      const social = config?.socialSignals || socialSignals;
      if (social?.facebookUrl) updateMetaTag('article:author', social.facebookUrl, true);
      if (social?.twitterUrl) updateMetaTag('twitter:creator', social.twitterUrl.replace('https://twitter.com/', '@'), true);
      if (social?.linkedinUrl) updateMetaTag('article:author', social.linkedinUrl, true);
    }

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:image', twitterImage, true);

    // Hreflang alternates are likewise emitted per-route by generateMetadata
    // (correct per-domain hosts + x-default) in the static HTML. The client must
    // NOT rewrite them — the old code deleted the correct server tags and re-added
    // config-based ones (e.g. all on climapri.com), corrupting per-domain hreflang.
    // Left to the server-rendered tags.

    // Structured data is NOT injected here any more. It is rendered into the
    // static HTML by the App Router page (src/utils/structuredData.ts), where
    // crawlers see it without having to run JavaScript first — this useEffect
    // only ran after hydration, so out/ shipped with no JSON-LD at all.
    //
    // Injecting it here as well would put a second, differently-shaped graph on
    // the same page (two Article nodes, two Organizations), which is worse than
    // either one alone. The `structuredData` prop is still accepted so the
    // existing callers compile unchanged; it is deliberately ignored. Move any
    // page-specific schema into structuredData.ts instead of reviving this.

    // Add Bing-specific viewport and compatibility tags
    let viewportTag = document.querySelector('meta[name="viewport"]');
    if (viewportTag) {
      viewportTag.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes');
    }
    
    // IE compatibility for Bing users
    updateMetaTag('http-equiv', 'IE=edge');
    
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogImage, ogType,
      twitterTitle, twitterDescription, twitterImage, structuredData, noIndex, hreflang,
      language, pageType, routeName, bingDescription, author, socialSignals]);

  return null;
};

// Utility function to generate breadcrumb structured data
export const generateBreadcrumbStructuredData = (breadcrumbs: Array<{name: string, url: string}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": breadcrumb.name,
      "item": breadcrumb.url
    }))
  };
};

// Utility function to generate FAQ structured data
export const generateFAQStructuredData = (faqs: Array<{question: string, answer: string}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Finnish language specific meta configurations
export const FinnishSEOConfigs = {
  homepage: {
    title: "ClimaPri IoT Ratkaisut - Älykkäät Vesimittarit, Kiinteistöautomaatio ja Energianhallinta",
    description: "Johtava suomalainen IoT-ratkaisujen toimittaja älykkäisiin rakennuksiin. ClimaPri tarjoaa langattomat vesimittarit, lattialämmityksen säätö (ClimaPri), palovaroittimet ja energianhallintajärjestelmät.",
    keywords: "älykkäät vesimittarit, IoT kiinteistöautomaatio, lattialämmityksen säätö, rakennusten mittausjärjestelmät, energianhallinta, palovaroittimet, kiinteistöhallinta IoT, ClimaPri, ClimaPri Cloud, langattomat sensorit, energiatehokkuus",
    hreflang: {
      'fi': 'https://climapri.com/fi/',
      'en': 'https://climapri.com/',
      'x-default': 'https://climapri.com/'
    }
  }
};

export default SEO;

// Export the SEO configurations for use in components
export { SEOConfigs };

// Export types for use in other components
export type { SEOProps, SEOLanguageConfig, SEOConfigType }; 